/**
 * Sprite pool — pre-allocates sprites to avoid create/destroy churn.
 * Uses a gear-shaped texture for the steampunk aesthetic.
 */

import { Container, Sprite, Texture } from 'pixi.js';

const POOL_SIZE = 700;
const GEAR_TEETH = 8;

export interface SpritePool {
  /** Get a sprite from the pool. Returns null if exhausted. */
  acquire(): Sprite | null;
  /** Return a sprite to the pool. */
  release(sprite: Sprite): void;
  /** Number of sprites currently in use */
  readonly activeCount: number;
  /** Total pool capacity */
  readonly capacity: number;
  /** Destroy all pool resources */
  destroy(): void;
}

/**
 * Create a gear-shaped texture for bubbles.
 * Generates a white gear cog that can be tinted to any color.
 *
 * Exported so effects that need to draw a "still on the board" gear
 * during a delay (cascade held gears, delayed drops) can spawn a Sprite
 * from this exact texture and be pixel-identical to the board sprite —
 * a `Graphics`-drawn stand-in reads as a different, uglier gear.
 */
export function createGearTexture(radius: number): Texture {
  const canvas = document.createElement('canvas');
  const pad = 2;
  const size = (radius + pad) * 2;
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const cx = size / 2;
  const cy = size / 2;

  // Draw gear shape: alternating inner/outer radius for teeth
  const outerR = radius;
  const innerR = radius * 0.78;
  const toothHalfAngle = Math.PI / (GEAR_TEETH * 2.5);

  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  for (let i = 0; i < GEAR_TEETH; i++) {
    const baseAngle = (i / GEAR_TEETH) * Math.PI * 2;
    const a1 = baseAngle - toothHalfAngle;
    const a2 = baseAngle + toothHalfAngle;
    const nextBase = ((i + 1) / GEAR_TEETH) * Math.PI * 2;
    const a4 = nextBase - toothHalfAngle;

    if (i === 0) {
      ctx.moveTo(cx + Math.cos(a1) * outerR, cy + Math.sin(a1) * outerR);
    }
    ctx.lineTo(cx + Math.cos(a2) * outerR, cy + Math.sin(a2) * outerR);
    ctx.lineTo(cx + Math.cos(a2) * innerR, cy + Math.sin(a2) * innerR);
    ctx.lineTo(cx + Math.cos(a4) * innerR, cy + Math.sin(a4) * innerR);
    ctx.lineTo(cx + Math.cos(a4) * outerR, cy + Math.sin(a4) * outerR);
  }
  ctx.closePath();
  ctx.fill();

  // Inner hub circle (subtle depth shading)
  ctx.fillStyle = 'rgba(0,0,0,0.12)';
  ctx.beginPath();
  ctx.arc(cx, cy, radius * 0.38, 0, Math.PI * 2);
  ctx.fill();

  // Axle hole
  ctx.fillStyle = 'rgba(0,0,0,0.25)';
  ctx.beginPath();
  ctx.arc(cx, cy, radius * 0.15, 0, Math.PI * 2);
  ctx.fill();

  return Texture.from(canvas);
}

/**
 * Create a sprite pool with pre-allocated gear sprites.
 * All sprites are added to the given container but hidden initially.
 */
export function createSpritePool(container: Container, radius: number): SpritePool {
  const texture = createGearTexture(radius);
  const available: Sprite[] = [];
  let active = 0;

  // Pre-allocate all sprites
  for (let i = 0; i < POOL_SIZE; i++) {
    const sprite = new Sprite(texture);
    sprite.anchor.set(0.5);
    sprite.visible = false;
    container.addChild(sprite);
    available.push(sprite);
  }

  return {
    acquire(): Sprite | null {
      const sprite = available.pop();
      if (!sprite) {
        console.warn('[SpritePool] Pool exhausted — all', POOL_SIZE, 'sprites in use');
        return null;
      }
      sprite.visible = true;
      active++;
      return sprite;
    },

    release(sprite: Sprite): void {
      sprite.visible = false;
      sprite.tint = 0xffffff;
      sprite.alpha = 1;
      sprite.scale.set(1);
      sprite.rotation = 0;
      sprite.position.set(0, 0);
      available.push(sprite);
      active--;
    },

    get activeCount(): number {
      return active;
    },

    get capacity(): number {
      return POOL_SIZE;
    },

    destroy(): void {
      for (const sprite of available) {
        sprite.destroy();
      }
      available.length = 0;
      active = 0;
      texture.destroy(true);
    },
  };
}
